import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { taskService, type TaskCommentWriteExtra } from '@/modules/tasks/services/task.service'
import { useAuth } from '@/hooks/useAuth'
import type {
  CommentCursorPage,
  Task,
  TaskChecklistItem,
  TaskComment,
  TaskCreateInput,
  TaskListParams,
  TaskReactionSummary,
  TaskUpdateInput,
  TaskUserRef,
} from '@/modules/tasks/types/task.types'

export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (params: TaskListParams) => [...taskKeys.lists(), params] as const,
  details: () => [...taskKeys.all, 'detail'] as const,
  detail: (id: string) => [...taskKeys.details(), id] as const,
  comments: (id: string) => [...taskKeys.all, 'comments', id] as const,
  activity: (id: string) => [...taskKeys.all, 'activity', id] as const,
  reminder: () => [...taskKeys.all, 'reminder'] as const,
  assignees: () => [...taskKeys.all, 'assignees'] as const,
}

type CommentsCache = { pages: CommentCursorPage[]; pageParams: unknown[] }

/** Same shape as messenger's patchMessageInCache — replaces one comment,
 * wholesale, wherever it is across every loaded page. Used after a mutation
 * response that recomputes a comment server-side (reaction, voice upload). */
function patchCommentInCache(
  qc: ReturnType<typeof useQueryClient>,
  id: string,
  commentId: string,
  patch: (row: TaskComment) => TaskComment,
) {
  qc.setQueryData(taskKeys.comments(id), (old: CommentsCache | undefined) => {
    if (!old) return old
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        items: page.items.map((row) => (row.id === commentId ? patch(row) : row)),
      })),
    }
  })
}

function invalidateTaskQueries(qc: ReturnType<typeof useQueryClient>, id?: string) {
  void qc.invalidateQueries({ queryKey: taskKeys.lists() })
  void qc.invalidateQueries({ queryKey: taskKeys.reminder() })
  if (id) void qc.invalidateQueries({ queryKey: taskKeys.detail(id) })
}

export function useTasks(params: TaskListParams, enabled = true) {
  return useQuery({
    queryKey: taskKeys.list(params),
    queryFn: ({ signal }) => taskService.list(params, { signal }),
    enabled,
    placeholderData: (previous) => previous,
  })
}

/** Header reminder chip — light polling via a short staleTime rather than a
 * second socket; refreshed on task mutations and live task.* WebSocket events. */
export function useTaskReminder(enabled: boolean) {
  return useQuery({
    queryKey: taskKeys.reminder(),
    queryFn: ({ signal }) => taskService.getReminder({ signal }),
    enabled,
    staleTime: 45_000,
  })
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: taskKeys.detail(id ?? ''),
    queryFn: ({ signal }) => taskService.getById(id!, { signal }),
    enabled: Boolean(id),
  })
}

function useOnTaskSaved() {
  const qc = useQueryClient()
  return (task: Task) => {
    qc.setQueryData(taskKeys.detail(task.id), task)
    invalidateTaskQueries(qc, task.id)
  }
}

export function useCreateTask() {
  const onSaved = useOnTaskSaved()
  return useMutation({
    mutationFn: (input: TaskCreateInput) => taskService.create(input),
    onSuccess: onSaved,
  })
}

export function useUpdateTask() {
  const onSaved = useOnTaskSaved()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TaskUpdateInput }) =>
      taskService.update(id, input),
    onSuccess: onSaved,
  })
}

export function useDeleteTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => taskService.delete(id),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: taskKeys.detail(id) })
      invalidateTaskQueries(qc)
    },
  })
}

export function useAddChecklistItem() {
  const onSaved = useOnTaskSaved()
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      taskService.addChecklistItem(id, title),
    onSuccess: onSaved,
  })
}

export function useUpdateChecklistItem() {
  const onSaved = useOnTaskSaved()
  return useMutation({
    mutationFn: ({
      id,
      itemId,
      input,
    }: {
      id: string
      itemId: string
      input: Partial<Pick<TaskChecklistItem, 'title' | 'completed' | 'position'>>
    }) => taskService.updateChecklistItem(id, itemId, input),
    onSuccess: onSaved,
  })
}

export function useDeleteChecklistItem() {
  const onSaved = useOnTaskSaved()
  return useMutation({
    mutationFn: ({ id, itemId }: { id: string; itemId: string }) =>
      taskService.deleteChecklistItem(id, itemId),
    onSuccess: onSaved,
  })
}

/** Newest-first cursor pagination, same shape as Messenger's useMessages —
 * page 0 is the latest ~30 comments; scrolling up fetches older pages. */
export function useTaskComments(id: string | undefined) {
  return useInfiniteQuery({
    queryKey: taskKeys.comments(id ?? ''),
    queryFn: ({ pageParam }) => taskService.listCommentsCursor(id!, { cursor: pageParam }),
    enabled: Boolean(id),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    // Light polling (same "polling over a dedicated socket" call as the
    // header reminder) so a sender's delivery/seen ticks catch up to a
    // recipient's activity without a manual refresh. Pauses automatically
    // once the tab loses focus/visibility (TanStack Query default).
    // Refetches every currently-loaded page, not just the first.
    refetchInterval: 20_000,
    // Override the app-wide refetchOnWindowFocus:false — a receipt (or the
    // WebSocket push announcing it) can land while this tab is backgrounded
    // (mobile lock screen, app switch), where both the poll above and the
    // WebSocket connection are commonly suspended by the OS/browser and
    // never catch up on their own. Regaining focus is exactly the moment to
    // resync instead of waiting on the next 20s tick (or forever, if the
    // socket silently dropped a non-persisted receipt push while away).
    refetchOnWindowFocus: true,
  })
}

/** WhatsApp-style "opened the Discussion" — fire-and-forget, no cache patch
 * needed: the viewer never renders ticks on their own read of someone
 * else's message, only the *other* person's next fetch reflects it. */
export function useMarkCommentsSeen() {
  return useMutation({
    mutationFn: ({ id, commentIds }: { id: string; commentIds: string[] }) =>
      taskService.markCommentsSeen(id, commentIds),
  })
}

/** Listener pressed play — patches this viewer's own cache immediately (so
 * the mic badge/waveform turn blue without waiting for the next poll). */
export function useMarkVoiceOpened() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, commentId }: { id: string; commentId: string }) =>
      taskService.markVoiceOpened(id, commentId),
    onSuccess: (_void, { id, commentId }) => {
      patchCommentInCache(qc, id, commentId, (row) => ({ ...row, voiceOpened: true }))
    },
  })
}

/** Same toggle/replace the server enforces (one reaction per user per
 * comment) — mirrored here purely for the optimistic patch below, so a tap
 * flips the chip instantly instead of waiting a round trip. The server
 * response (onSuccess) always overwrites this guess with the real state. */
function optimisticToggleReactions(
  reactions: TaskReactionSummary[],
  emoji: string,
  me: TaskUserRef,
): TaskReactionSummary[] {
  const wasMine = reactions.find((r) => r.mine)?.emoji
  const withoutMe = reactions
    .map((r) => (r.mine ? { ...r, mine: false, count: r.count - 1, users: r.users.filter((u) => u.id !== me.id) } : r))
    .filter((r) => r.count > 0)
  if (wasMine === emoji) return withoutMe
  const existingIndex = withoutMe.findIndex((r) => r.emoji === emoji)
  if (existingIndex === -1) return [...withoutMe, { emoji, count: 1, mine: true, users: [me] }]
  return withoutMe.map((r, i) =>
    i === existingIndex ? { ...r, mine: true, count: r.count + 1, users: [...r.users, me] } : r,
  )
}

/** Quick-react toggle/replace. Optimistic — flips the tapped chip
 * immediately, rolls back on error, and always reconciles with the
 * server's recomputed `reactions` on success (the source of truth other
 * members' live pushes will eventually match). */
export function useToggleReaction() {
  const qc = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: ({ id, commentId, emoji }: { id: string; commentId: string; emoji: string }) =>
      taskService.toggleReaction(id, commentId, emoji),
    onMutate: async ({ id, commentId, emoji }) => {
      if (!user) return undefined
      await qc.cancelQueries({ queryKey: taskKeys.comments(id) })
      const previous = qc.getQueryData<CommentsCache>(taskKeys.comments(id))
      patchCommentInCache(qc, id, commentId, (row) => ({
        ...row,
        reactions: optimisticToggleReactions(row.reactions, emoji, user),
      }))
      return { previous }
    },
    onError: (_err, { id }, context) => {
      if (context?.previous) qc.setQueryData(taskKeys.comments(id), context.previous)
    },
    onSuccess: (comment, { id, commentId }) => {
      patchCommentInCache(qc, id, commentId, () => comment)
    },
  })
}

export function useAddTaskComment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      content,
      ...extra
    }: { id: string; content: string } & TaskCommentWriteExtra) =>
      taskService.addComment(id, content, extra),
    onSuccess: (comment, { id }) => {
      // Appends into the newest (first) page — same "optimistic-ish, real
      // response, no reload" feel as Messenger's useSendChatMessage.
      qc.setQueryData(taskKeys.comments(id), (old: CommentsCache | undefined) => {
        const first = old?.pages[0]
        if (!first) return old
        if (first.items.some((row) => row.id === comment.id)) return old
        const pages = [...old.pages]
        pages[0] = { ...first, items: [...first.items, comment], nextCursor: first.nextCursor ?? null }
        return { ...old, pages }
      })
      void qc.invalidateQueries({ queryKey: taskKeys.comments(id) })
      void qc.invalidateQueries({ queryKey: taskKeys.activity(id) })
      void qc.invalidateQueries({ queryKey: taskKeys.detail(id) })
    },
  })
}

export function useUpdateTaskComment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      commentId,
      content,
      ...extra
    }: {
      id: string
      commentId: string
      content: string
    } & TaskCommentWriteExtra) => taskService.updateComment(id, commentId, content, extra),
    onSuccess: (_comment, { id }) => {
      void qc.invalidateQueries({ queryKey: taskKeys.comments(id) })
    },
  })
}

export function useDeleteTaskComment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, commentId }: { id: string; commentId: string }) =>
      taskService.deleteComment(id, commentId),
    onSuccess: (_void, { id }) => {
      void qc.invalidateQueries({ queryKey: taskKeys.comments(id) })
    },
  })
}

/** Second step of send — attaches audio to an already-created voice-stub
 * comment. Separate from useAddTaskComment so a failed upload can be
 * retried without creating a duplicate comment. */
export function useUploadTaskVoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      commentId,
      blob,
      mimeType,
    }: {
      id: string
      commentId: string
      blob: Blob
      mimeType: string
    }) => taskService.uploadCommentVoice(id, commentId, blob, mimeType),
    onSuccess: (comment, { id, commentId }) => {
      // Flip voiceAvailable immediately so other viewers / this tab don't
      // stay stuck on the "device-only" fallback until a slow refetch.
      patchCommentInCache(qc, id, commentId, (row) => ({ ...row, ...comment, voiceAvailable: true }))
      void qc.invalidateQueries({ queryKey: taskKeys.comments(id) })
    },
  })
}

/** Active users for assignee pickers — GET /tasks/assignees, not /users. */
export function useTaskUserOptions(enabled: boolean) {
  return useQuery({
    queryKey: taskKeys.assignees(),
    queryFn: ({ signal }) => taskService.listAssignees({ signal }),
    enabled,
    staleTime: 60_000,
  })
}

export function useTaskActivity(id: string | undefined) {
  return useQuery({
    queryKey: taskKeys.activity(id ?? ''),
    queryFn: ({ signal }) => taskService.listActivity(id!, {}, { signal }),
    enabled: Boolean(id),
  })
}
